import { Tooltip } from '@mui/material';

import type { IDialogTitleTruncatedProps } from './types';
import { Title, TitleWrapper } from './styles';

/**
 * Component provides features like text truncation with a tooltip for long titles and flexible styling.
 *
 * @param {IDialogTitleTruncatedProps} props - The properties for configuring the dialog title.
 *
 * @description
 * This component automatically includes a **title wrapper** (`TitleWrapper`, a styled Box component)
 * for consistent layout.
 *
 * You can pass **any additional React nodes** (like icons, buttons, etc.)
 * alongside the main title via the `children` prop within `titleWrapperProps`.
 *
 * **Directly pass props** to the internal Material-UI `Typography` component for the title
 * via `titleProps`. Similarly, use `titleWrapperProps` to pass additional props
 * to the internal styled `Box` component that acts as the title wrapper.
 */
export const DialogTitleTruncated = ({
  title,
  titleProps = {},
  titleWrapperProps = {},
  maxVisibleTitleSymbols = 50,
  maxWrapperWidth,
  styles = {},
}: IDialogTitleTruncatedProps) => {
  const { titleWrapper: titleWrapperCSS, title: titleCSS } = styles;
  const { children: titleWrapperChildren, offset = 0, ...titleWrapperPropsRest } = titleWrapperProps;
  const titleIsString = typeof title === 'string';
  const titleLength = titleIsString ? title.length : maxVisibleTitleSymbols + 1;
  const showTooltip = titleLength > maxVisibleTitleSymbols;

  const titleNode = (
    <Title variant='h6' noWrap {...titleProps} $css={titleCSS}>
      {title}
    </Title>
  );

  return (
    <Tooltip title={showTooltip ? title : ''} placement='top' followCursor>
      <TitleWrapper
        {...titleWrapperPropsRest}
        $offset={offset}
        $maxWidth={maxWrapperWidth ?? 'sm'}
        $css={titleWrapperCSS}
      >
        {titleNode}
        {titleWrapperChildren}
      </TitleWrapper>
    </Tooltip>
  );
};
